import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import { DISCARD_UNSAVED_ENTITY_NAV_CONFIRM } from '../constants/pendingChangesText';

const NavigationGuardContext = createContext(null);

const noopGuardPredicate = () => false;

export function useNavigationGuard() {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }
  return context;
}

/**
 * Must render inside <Router> so it can wrap the router navigator.
 */
export function NavigationGuardProvider({ children }) {
  const parentNavigation = useContext(NavigationContext);
  const guardPredicateRef = useRef(noopGuardPredicate);
  const bypassGuardRef = useRef(false);
  const [blockedRequest, setBlockedRequest] = useState(null);

  const registerGuardPredicate = useCallback((predicate) => {
    guardPredicateRef.current =
      typeof predicate === 'function' ? predicate : noopGuardPredicate;
  }, []);

  const unregisterGuardPredicate = useCallback(() => {
    guardPredicateRef.current = noopGuardPredicate;
    setBlockedRequest(null);
  }, []);

  const shouldBlockNavigation = useCallback(() => {
    try {
      return Boolean(guardPredicateRef.current());
    } catch {
      return false;
    }
  }, []);

  const guardAction = useCallback(
    (action) => {
      if (bypassGuardRef.current || !shouldBlockNavigation()) {
        action();
        return false;
      }

      setBlockedRequest({ action });
      return true;
    },
    [shouldBlockNavigation]
  );

  const wrappedNavigator = useMemo(() => {
    const navigator = parentNavigation?.navigator;
    if (!navigator) {
      return navigator;
    }

    const wrap =
      (method) =>
      (...args) => {
        guardAction(() => method.apply(navigator, args));
      };

    return {
      ...navigator,
      push: wrap(navigator.push.bind(navigator)),
      replace: wrap(navigator.replace.bind(navigator)),
      go: wrap(navigator.go.bind(navigator)),
    };
  }, [parentNavigation?.navigator, guardAction]);

  const navigationContext = useMemo(() => {
    if (!parentNavigation) {
      return parentNavigation;
    }

    return {
      ...parentNavigation,
      navigator: wrappedNavigator,
    };
  }, [parentNavigation, wrappedNavigator]);

  const proceedBlocked = useCallback(() => {
    const action = blockedRequest?.action;
    setBlockedRequest(null);
    bypassGuardRef.current = true;
    try {
      action?.();
    } finally {
      bypassGuardRef.current = false;
    }
  }, [blockedRequest]);

  const cancelBlocked = useCallback(() => {
    setBlockedRequest(null);
  }, []);

  const value = useMemo(
    () => ({
      registerGuardPredicate,
      unregisterGuardPredicate,
      guardAction,
      blockedRequest,
      proceedBlocked,
      cancelBlocked,
    }),
    [
      registerGuardPredicate,
      unregisterGuardPredicate,
      guardAction,
      blockedRequest,
      proceedBlocked,
      cancelBlocked,
    ]
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      <NavigationContext.Provider value={navigationContext}>
        {children}
        {blockedRequest ? (
          <ConfirmationModal
            isOpen
            onClose={cancelBlocked}
            onConfirm={proceedBlocked}
            title={DISCARD_UNSAVED_ENTITY_NAV_CONFIRM.title}
            message={DISCARD_UNSAVED_ENTITY_NAV_CONFIRM.message}
            confirmText={DISCARD_UNSAVED_ENTITY_NAV_CONFIRM.confirmText}
            cancelText={DISCARD_UNSAVED_ENTITY_NAV_CONFIRM.cancelText}
          />
        ) : null}
      </NavigationContext.Provider>
    </NavigationGuardContext.Provider>
  );
}
